// The LABEL cross-roster overview — the "All roster" dashboard an org seat lands on. Composed, reach-scoped,
// MONEY-FREE by construction (the DTO has no money field; the FE never renders one). Four-state contract
// (loading / error+retry / born-blank / content); an unassigned staffer (artistCount 0) sees the designed
// empty rail, never fabricated numbers.
//
// Section order (ART-335, product owner): header → the release cards → Needs attention → Trending (the top three
// spikes) → five-tile KPI strip → [Roster pulse | To-Do]. Pulse and To-Do pair because they are the two lists an
// org seat scans.
//
// Three cards were removed in that pass, none of them in the specimen: the Antenna summary (a skeleton with
// no aggregation endpoint — its card is deleted, though `RosterOverviewRead.Antenna` still composes null
// server-side, which is where the null-vs-empty distinction that made it worth shipping is recorded),
// the Cross-roster to-dos table (still mounted on the Management lens, which is where the roster's shared
// task board belongs) and the Incoming-connections inbox (person-scoped, and reachable from the artist
// shell). Card SUBTITLES went with them — the four titles say what they are.
//
// TWO DELIBERATE DIVERGENCES from the legacy specimen, both because the legacy tile was decoration:
//   • NO "Roster / All roster" crumb over the h1. The product-owner ruling of 2026-08-17 stands (the
//     category label was repeating what the nav already said) and `headings.guard.test.ts` enforces it. The
//     org is named by the rail and the account chip either way — and by this h1.
//   • NO "Last 28 days" window control. Legacy renders it as a `<span>`, with no handler, no state and no
//     second window to pick: a dead affordance that promises the strip can be re-scoped. Every figure here
//     is fixed-window by its own source (streams·28d, releases·28d, a 7-day growth rank), so the honest
//     move is to say the window in each label rather than offer a picker that changes nothing.
// Refresh IS real — it revalidates the composed read, the one action legacy's header actually wired.
import { useState } from "react";
import { Users } from "lucide-react";
import {
  AsOfMarker, Button, Card, EmptyState, Icon, Sparkline, StatBlock, StatDelta,
  compactNumber,
} from "@cntrl/ui";
import { useOrgContext, useOrgCapability } from "../../app/orgContext.js";
import { RosterScreenShell, type RosterScreenState } from "./RosterScreenShell.js";
import { openTeamPanel } from "../../app/teamPanel.js";
import { useOrg } from "../../app/hooks/useOrg.js";
import { useRosterOverview, useRosterNeedsAttention, useRosterArtists } from "../../app/hooks/useRoster.js";
import { NeedsAttentionCard } from "./NeedsAttentionCard.js";
import { useDirectoryPeople } from "../tasks/taskPeople.js";
import { TaskListRow } from "../tasks/TaskListRow.js";
import { asTaskSummary, sortTasksForList, taskRowsClassName } from "../tasks/taskRow.js";
import { TaskCreateModal } from "../tasks/TaskCreateModal.js";
import { RosterBandProvider } from "../../shells/roster/RosterBandProvider.js";
import { AddButton } from "./rosterArtistCards.js";
import { NewOfferBandPicker } from "./NewOfferBandPicker.js";
import { ReleaseMiniCards } from "./ReleaseCards.js";
import { TrendingPillList, TrendingTitle } from "../dashboard/Trending.js";
import { useRosterReleaseCards } from "../../app/hooks/useReleaseCards.js";
import { ArtistMark } from "./ArtistMark.js";
import styles from "./RosterHomeScreen.module.scss";

/** The header's roster line — legacy's "3 artists" head-meta, pluralized. */
function rosterLine(artistCount: number): string {
  return `${artistCount} ${artistCount === 1 ? "artist" : "artists"}`;
}

export function RosterHomeScreen({
  onNavigate,
  onOpenTask,
}: {
  onNavigate?: (route: string) => void;
  onOpenTask?: (bandId: string, taskId: string) => void;
}) {
  const { orgId } = useOrgContext();
  const canManageTeam = useOrgCapability("roster:manage_seats");
  const org = useOrg(orgId);
  const overview = useRosterOverview(orgId);
  const attention = useRosterNeedsAttention(orgId);
  const releaseCards = useRosterReleaseCards(orgId);
  const name = org.data?.name || "your organization";
  const data = overview.data;

  // The generated types mark every composed field optional/nullable (Swashbuckle non-required), so read defensively.
  const artistCount = data?.header?.artistCount ?? 0;
  const lastRefresh = data?.header?.lastRefresh;
  const kpis = data?.kpis;
  const pulse = data?.pulse ?? [];
  // The top three spikes across the roster (ART-335), each the artist dashboard's own item so a row opens the
  // same pop-up; whose spike it is rides beside it. The server already ranks them, biggest lift first.
  const trendingRows = (data?.trending ?? []).filter((t) => t.item).slice(0, 3);
  const trendingItems = trendingRows.map((t) => t.item!);
  const trendingArtist = new Map(trendingRows.map((t) => [t.item!, t.artistName] as const));
  const tasks = data?.tasks;
  const taskItems = sortTasksForList((tasks?.items ?? []).map(asTaskSummary));
  const artistNameOf = new Map((tasks?.items ?? []).map((t) => [t.taskId, t.artistName] as const));
  // The global To-Do's red + (ART-334): pick the artist the task is for, then the ordinary create form, mounted
  // under that artist (the scope page has no band of its own — the "+ New offer" shape).
  const artists = useRosterArtists(orgId);
  const [pickingArtist, setPickingArtist] = useState(false);
  const [taskForBand, setTaskForBand] = useState<string | null>(null);
  // WHO EACH ROW IS ON (ART-122). For a seat manager this panel is the firm's work across the roster rather
  // than their own, so a row has to name its assignee — and the payload's directory is the only thing that
  // can, since these are org colleagues holding no band seat and there is no band mounted here at all.
  const taskPeople = useDirectoryPeople(tasks?.people ?? undefined);
  const loaded = !overview.isLoading && !!data;

  const openArtist = (bandId: string | null | undefined) => bandId && onNavigate?.(`/roster/artists/${bandId}`);
  // A to-do row opens the TASK. `onOpenTask` is optional and an unidentified row has no task to open, so
  // either gap falls back to the artist — the row's previous behaviour, kept as the remainder rather than as
  // the rule.
  const openTask = (bandId: string | null | undefined, taskId: string | null | undefined) =>
    bandId && taskId && onOpenTask ? onOpenTask(bandId, taskId) : openArtist(bandId);

  // The four states, resolved from THIS lens's own read. Born-blank is `artistCount === 0`: every card on
  // this page is a roster aggregate, so with no reachable artist there is nothing for any of them to say.
  const state: RosterScreenState = overview.isLoading
    ? { status: "loading" }
    : overview.isError
      ? { status: "error", error: overview.error, onRetry: () => void overview.refetch() }
      : !data || artistCount === 0
        ? {
            status: "empty",
            title: "No artists yet",
            description:
              "Artists appear here once they link your organization and assign you to their team. Nothing to show until then.",
            // Team is `roster:manage_seats`. Without it this was a door onto a permission wall, so a
            // staffer gets the empty state's guidance and no button — the same rule the account menu's
            // Team entry now follows.
            action: canManageTeam ? (
              <Button variant="ghost" onClick={() => openTeamPanel()}>
                <Icon icon={Users} size={16} /> Manage team
              </Button>
            ) : undefined,
          }
        : { status: "ready" };

  return (
    <RosterScreenShell
      title={name}
      sub={
        <>
          {/* The roster line waits for the read: "0 artists" is a definite claim about the org, and
              rendering it off a not-yet-loaded count states it to every staffer for the length of the
              fetch — the born-blank page and the loading page must never look alike. */}
          {loaded && <span>{rosterLine(artistCount)}</span>}
          {/* `AsOfMarker`, the shipped staleness primitive, rather than a fourth hand-rolled span: it
              renders NOTHING without a stamp, which is the honest form for a roster that has never landed
              analytics (legacy printed "Not synced yet", which reads as a fault). Label and Booking said
              this same fact in two different words — "synced 20h ago" and "last sync 20h ago" — which is
              what a primitive existing and not being used costs. */}
          {loaded && <AsOfMarker asOf={lastRefresh} />}
        </>
      }
      actions={
        /* Background revalidation, no spinner wipe — the composed read stays on screen while it refetches,
           so the page never flashes back through its own skeleton. Disabled while one is in flight. */
        <Button variant="ghost" onClick={() => void overview.refetch()} disabled={overview.isFetching}>
          Refresh
        </Button>
      }
      state={state}
    >
      <>
          {/* Release cards (ART-335) — each artist's soonest upcoming release, else their most recent; a card opens
              that artist. Absent until the read lands, and absent for a roster with no dated release. */}
          <ReleaseMiniCards
            cards={releaseCards.data ?? []}
            onOpenArtist={(bandId) => onNavigate?.(`/roster/artists/${bandId}`)}
          />

          {/* KPI strip — legacy's five label-roster tiles, in its order and with its labels.
              (`LabelKpis` in roster-sections/parts.tsx; the wired values come from the worker's
              handleLabelDashboardOverview.)

              Two of the five are STRUCTURALLY '—' and that is faithful, not unfinished: we hold no windowed
              radio spin count (radio_stats_current is lifetime, artist-grain) and there is no ads product to
              count campaigns in. `StatBlock` owns the null → '—' contract, so the server composing them null
              is the whole implementation. Legacy shipped both the same way.

              The three that ARE wired improve on it. Legacy's "Streams · 7D" is `streams_28d / 4` — an
              estimate under a measurement's label, which can never move differently from the 28-day figure
              it divides. Ours is the real rise in the cumulative `streams_total` across the last seven days,
              with a week-over-week delta legacy never computed at all. */}
          <div className={styles.kpiRow}>
            <StatBlock label="Streams · 7d" value={kpis?.streams7d?.value} delta={kpis?.streams7d?.deltaPct} />
            <StatBlock label="Monthly listeners" value={kpis?.monthlyListeners?.value} delta={kpis?.monthlyListeners?.deltaPct} />
            <StatBlock label="Radio spins · 7d" value={kpis?.radioSpins7d?.value} />
            <StatBlock label="Live campaigns" value={kpis?.liveCampaigns?.value} exact />
            <StatBlock label="Releases · 28d" value={kpis?.releases28d?.value} delta={kpis?.releases28d?.deltaPct} exact />
          </div>

          {/* Needs attention beside Trending (ART-334, product owner), then Roster pulse beside To-Do. */}
          <div className={styles.twoUp}>
            {/* Needs attention (ART-324) — each artist's most pressing item, usually the nearest release with work
                still open. A label reads the schedule but does not edit it, so no row offers Open. */}
            <NeedsAttentionCard
              scope="roster"
              items={attention.data?.items}
              total={attention.data?.total}
              isLoading={attention.isLoading}
              onOpenArtist={(bandId) => onNavigate?.(`/roster/artists/${bandId}`)}
            />

            {/* Trending (ART-335) — the roster's top three spikes. A pill opens the spike's pop-up, naming the
                artist. */}
            <Card id="roster-trending" className={styles.scrollCard} title={<TrendingTitle count={trendingItems.length} />}>
              {trendingItems.length === 0 ? (
                <p className={styles.note}>No spikes right now</p>
              ) : (
                // The artist dashboard's own pills, each naming the artist in its meta line and its pop-up.
                <TrendingPillList items={trendingItems} artistOf={(it) => trendingArtist.get(it)} />
              )}
            </Card>
          </div>

          <div className={styles.twoUp}>
            {/* Roster Pulse — every reachable artist, ranked by 7-day stream growth. The badge is the header's
                `artistCount`, and the list is UNCAPPED so the two describe the same set: a count over a
                silently-truncated list is the ART-100 failure, and counting around the truncation was only
                half of that fix. The card scrolls past ~6 rows instead. */}
            <Card
              id="roster-pulse"
              className={styles.scrollCard}
              title="Roster pulse"
            >
              {pulse.length === 0 ? (
                <EmptyState title="No streaming movement yet" description="Growth appears once your artists have a few days of streaming history." />
              ) : (
                <ul className={styles.pulseList}>
                  {pulse.map((row, i) => (
                    <li key={row.bandId ?? i}>
                      <button type="button" className={styles.pulseRow} onClick={() => openArtist(row.bandId)}>
                        <ArtistMark name={row.name} bandId={row.bandId} avatarMediaId={row.avatarMediaId} size="sm" />
                        <span className={styles.pulseText}>
                          <span className={styles.pulseName}>{row.name}</span>
                          {/* The row's own figure, from its own field — not `sparkline[^1]`, which is the
                              shape it is drawn from rather than the number it reports. */}
                          <span className={styles.pulseMeta}>
                            {row.streams28d != null ? `${compactNumber(row.streams28d)} streams` : "—"} · 28d
                          </span>
                        </span>
                        <Sparkline values={row.sparkline ?? []} width={72} height={24} area label={`${row.name ?? "artist"} streaming trend`} />
                        {/* A born-blank artist (<2 snapshots) gets a dash, never "+0.0%" — the sentinel rule
                            the legacy worker's `trend: 0` made necessary and ours makes structural (null). */}
                        {row.growthPct != null ? <StatDelta value={row.growthPct} /> : <span className={styles.noDelta}>—</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* To-Do — the GLOBAL to-do box (ART-334): the org's work across the roster, the red + to add one.
                No counts in the head (product owner). The rows are the artist side's task row, a size down, with
                the artist the task is on as the sub-line. */}
            <Card
              id="roster-tasks"
              className={styles.scrollCard}
              title="To-Do"
              actions={<AddButton label="New task" onClick={() => setPickingArtist(true)} />}
            >
              {taskItems.length === 0 ? (
                <EmptyState
                  title="You're all caught up"
                  // The panel stopped being person-bound for a seat manager on 2026-09-08 (IOrgTaskAudience),
                  // and this line did not move with it — it told a manager the card was their own
                  // assignments while the rows beside it were the whole firm's.
                  description="Your org's work across your roster's artists shows up here."
                />
              ) : (
                <div className={styles.todoBleed}>
                  <ul className={taskRowsClassName(styles.taskList)}>
                    {taskItems.map((t, i) => (
                      // Opens the TASK (ART-119), not just its artist; a row with no id falls back to the artist.
                      <TaskListRow
                        key={t.taskId ?? i}
                        task={t}
                        people={taskPeople}
                        sub={artistNameOf.get(t.taskId) ?? null}
                        density="compact"
                        onOpen={() => openTask(t.bandId, t.taskId)}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>

        {pickingArtist && (
          <NewOfferBandPicker
            title="New task"
            linkType={null}
            emptyTitle="No artists yet"
            emptyDescription="A task is made on an artist on your roster. None are here yet."
            artists={artists.data ?? []}
            onSelect={(bandId) => {
              setPickingArtist(false);
              setTaskForBand(bandId);
            }}
            onClose={() => setPickingArtist(false)}
          />
        )}
        {taskForBand && (
          <RosterBandProvider artistId={taskForBand}>
            <TaskCreateModal
              onClose={() => setTaskForBand(null)}
              onCreated={(taskId) => {
                // Close, repaint, then open the new task (ART-343) — the modal leaves the close to its host.
                const bandId = taskForBand;
                setTaskForBand(null);
                void overview.refetch();
                if (bandId && onOpenTask) onOpenTask(bandId, taskId);
              }}
            />
          </RosterBandProvider>
        )}
      </>
    </RosterScreenShell>
  );
}
